import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, FileText } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-14">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="bg-[#1a1f2e] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" data-id="element-15">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-16">
          <div className="flex items-center gap-3" data-id="element-17">
            {type === 'privacy' ? <Shield className="w-5 h-5 text-[#00d4ff]" data-id="element-18" /> : <FileText className="w-5 h-5 text-[#00d4ff]" data-id="element-19" />}
            <h2 className="text-xl font-bold text-white" data-id="element-20">
              {type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors text-slate-400 hover:text-white" data-id="element-21">
            <X className="w-5 h-5" data-id="element-22" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-slate-300 text-sm leading-relaxed space-y-4" data-id="element-23">
          {type === 'privacy' ? <PrivacyContent data-id="element-24" /> : <TermsContent data-id="element-25" />}
        </div>
      </motion.div>
    </motion.div>;
}
function PrivacyContent() {
  return <>
      <p className="text-slate-400 text-xs uppercase tracking-wider font-bold" data-id="element-26">
        Última actualización: Marzo 2026
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-27">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-28">
        Recopilamos información personal que usted nos proporciona directamente
        al realizar una compra, como su nombre, dirección de correo electrónico
        y datos de pago. Los datos de pago son procesados de forma segura por
        nuestro proveedor de pagos y no almacenamos información de tarjetas de
        crédito en nuestros servidores.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-29">
        2. Uso de la Información
      </h3>
      <p data-id="element-30">
        Utilizamos su información personal para: procesar su compra y entregar
        el producto digital, enviar comunicaciones relacionadas con su pedido,
        mejorar nuestros productos y servicios, y cumplir con obligaciones
        legales.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-31">
        3. Protección de Datos
      </h3>
      <p data-id="element-32">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger su información personal contra acceso no autorizado,
        alteración, divulgación o destrucción. Todas las transacciones se
        realizan a través de conexiones cifradas (SSL/TLS).
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-33">4. Cookies</h3>
      <p data-id="element-34">
        Nuestro sitio web puede utilizar cookies y tecnologías similares para
        mejorar su experiencia de navegación y analizar el tráfico del sitio.
        Puede configurar su navegador para rechazar cookies, aunque esto podría
        afectar la funcionalidad del sitio.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-35">5. Terceros</h3>
      <p data-id="element-36">
        No vendemos, intercambiamos ni transferimos su información personal a
        terceros, excepto a proveedores de servicios de confianza que nos
        asisten en la operación del sitio web y el procesamiento de pagos,
        siempre que dichas partes acuerden mantener esta información
        confidencial.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-37">6. Sus Derechos</h3>
      <p data-id="element-38">
        Usted tiene derecho a acceder, rectificar o eliminar su información
        personal. Para ejercer estos derechos, puede contactarnos a través de
        los canales indicados en este sitio.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-39">
        7. Cambios a esta Política
      </h3>
      <p data-id="element-40">
        Nos reservamos el derecho de actualizar esta política de privacidad en
        cualquier momento. Los cambios serán publicados en esta página con la
        fecha de actualización correspondiente.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-slate-400 text-xs uppercase tracking-wider font-bold" data-id="element-41">
        Última actualización: Marzo 2026
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-42">
        1. Aceptación de los Términos
      </h3>
      <p data-id="element-43">
        Al acceder y utilizar este sitio web y adquirir el producto "Potencia
        21", usted acepta estar sujeto a estos términos y condiciones. Si no
        está de acuerdo con alguno de estos términos, le rogamos que no utilice
        nuestro sitio ni adquiera nuestros productos.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-44">
        2. Naturaleza del Producto
      </h3>
      <p data-id="element-45">
        Potencia 21 es un producto digital de carácter informativo y educativo.
        No constituye consejo médico, diagnóstico ni tratamiento. Siempre
        consulte a un profesional de la salud antes de realizar cambios en su
        estilo de vida, dieta o rutina de ejercicios.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-46">
        3. Entrega del Producto
      </h3>
      <p data-id="element-47">
        Al completar su compra, recibirá acceso inmediato al contenido digital a
        través del correo electrónico proporcionado. Es su responsabilidad
        asegurar que la dirección de correo electrónico sea correcta.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-48">
        4. Política de Reembolso
      </h3>
      <p data-id="element-49">
        Debido a la naturaleza digital del producto y su entrega inmediata,
        todas las ventas son finales. No se ofrecen reembolsos una vez que el
        contenido ha sido entregado y accedido.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-50">
        5. Propiedad Intelectual
      </h3>
      <p data-id="element-51">
        Todo el contenido incluido en Potencia 21 está protegido por derechos de
        autor. Queda prohibida la reproducción, distribución, modificación o uso
        comercial del material sin autorización expresa por escrito.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-52">
        6. Limitación de Responsabilidad
      </h3>
      <p data-id="element-53">
        Los resultados pueden variar según el individuo. No garantizamos
        resultados específicos. En ningún caso seremos responsables por daños
        directos, indirectos, incidentales o consecuentes derivados del uso de
        este producto.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-54">
        7. Descargo de Responsabilidad Médica
      </h3>
      <p data-id="element-55">
        Este producto no está diseñado para diagnosticar, tratar, curar o
        prevenir ninguna enfermedad. Las declaraciones contenidas no han sido
        evaluadas por ninguna autoridad sanitaria. Consulte siempre a su médico
        antes de comenzar cualquier programa de salud.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-56">8. Ley Aplicable</h3>
      <p data-id="element-57">
        Estos términos se regirán e interpretarán de acuerdo con las leyes
        aplicables en la jurisdicción correspondiente, sin dar efecto a ningún
        principio de conflicto de leyes.
      </p>
    </>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 text-center text-slate-500 text-sm border-t border-white/5 bg-[#0a0e1a]" data-id="element-58">
        <p data-id="element-59">
          © {new Date().getFullYear()} Potencia 21. Todos los derechos
          reservados.
        </p>

        <div className="flex items-center justify-center gap-4 mt-3" data-id="element-60">
          <button onClick={() => setActiveModal('privacy')} className="text-slate-500 hover:text-[#00d4ff] transition-colors underline underline-offset-2 decoration-slate-700 hover:decoration-[#00d4ff]/50" data-id="element-61">
            Política de Privacidad
          </button>
          <span className="text-slate-700" data-id="element-62">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-slate-500 hover:text-[#00d4ff] transition-colors underline underline-offset-2 decoration-slate-700 hover:decoration-[#00d4ff]/50" data-id="element-63">
            Términos y Condiciones
          </button>
        </div>

        <p className="mt-3 text-xs max-w-2xl mx-auto px-4 opacity-60" data-id="element-64">
          Este producto no sustituye el consejo médico profesional. Los
          resultados pueden variar dependiendo del compromiso y la biología
          individual.
        </p>
      </footer>

      <AnimatePresence data-id="element-65">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-66" />}
      </AnimatePresence>
    </>;
}